import dkoApi from '@/api/v1/dkoCooperation';
import router from '@/router/index';
import utils from '@/helpers/utils';

let latestListRequest = 0;

export default {
  namespaced: true,

  state: {
    list: [],
    listUnavailable: false,
    listQuery: {
      status: 'pending',
      forPrint: false,
      query: null,
      purpose: null,
      departmentId: null,
    },
    request: null,
    actions: [],
    candidates: [],
    isProcessing: false,
  },

  mutations: {
    SET_LIST(state, list) {
      state.list = list;
    },
    SET_LIST_UNAVAILABLE(state, unavailable) {
      state.listUnavailable = unavailable;
    },
    SET_LIST_QUERY(state, query) {
      state.listQuery = { ...state.listQuery, ...query };
    },
    SET_REQUEST(state, request) {
      state.request = request;
    },
    SET_ACTIONS(state, actions) {
      state.actions = actions;
    },
    SET_CANDIDATES(state, candidates) {
      state.candidates = candidates;
    },
    SET_PROCESSING(state, isProcessing) {
      state.isProcessing = isProcessing;
    },
  },

  actions: {
    fetchList({ commit, dispatch, state, rootState }) {
      const requestId = ++latestListRequest;
      const isCurrent = () => requestId === latestListRequest;

      commit('CHANGE_LOADING_STATUS', true, { root: true });
      if (!state.listQuery.forPrint) commit('SET_LIST_UNAVAILABLE', false);
      const { pagination, itemsPerPage } = rootState.pagination;
      const { status, forPrint, query, purpose, departmentId } =
        state.listQuery;
      const filters = { status: forPrint ? 'pending' : status };
      if (query) filters.query = query;
      if (purpose) filters.purpose = purpose;
      if (departmentId) filters.departmentIds = [departmentId];

      const params = {
        page: pagination.page || 1,
        itemsPerPage,
        filters,
      };
      const request = forPrint
        ? dkoApi.printCoordination(params)
        : dkoApi.index(params);

      return request
        .then((response) => {
          if (!isCurrent()) return;

          commit('SET_LIST_UNAVAILABLE', false);
          commit('SET_LIST', response.data.collection);
          commit('pagination/SET_PAGINATION', response.data.pagination, {
            root: true,
          });
        })
        .catch((error) => {
          if (!isCurrent()) return;
          if (!(forPrint && error.response?.status === 404)) {
            return Promise.reject(error);
          }

          commit('SET_LIST', []);
          commit('SET_LIST_UNAVAILABLE', true);
          commit(
            'pagination/SET_PAGINATION',
            {
              page: 1,
              itemsLength: 0,
              pageCount: 0,
            },
            { root: true }
          );
          return dispatch(
            'snackbar/showSnackbar',
            {
              message: ['Nie udało się pobrać listy dokumentów do druku.'],
              type: 'error',
            },
            { root: true }
          );
        })
        .finally(() => {
          if (!isCurrent()) return;
          commit('CHANGE_LOADING_STATUS', false, { root: true });
        });
    },

    setListQuery({ commit, dispatch }, query) {
      commit('SET_LIST_QUERY', query);
      dispatch('pagination/setPage', 1, { root: true });
      return dispatch('fetchList');
    },

    fetchOne({ commit }, id) {
      commit('CHANGE_LOADING_STATUS', true, { root: true });
      return dkoApi
        .show(id)
        .then((response) => {
          if (String(router.currentRoute.params.id) === String(id)) {
            commit('SET_REQUEST', response.data);
          }
          return response.data;
        })
        .finally(() => {
          commit('CHANGE_LOADING_STATUS', false, { root: true });
        });
    },

    fetchActions({ commit }, id) {
      return dkoApi.actions(id).then((response) => {
        if (String(router.currentRoute.params.id) === String(id)) {
          commit('SET_ACTIONS', response.data);
        }
        return response.data;
      });
    },

    fetchCandidates({ commit }) {
      return dkoApi.candidateUsers().then((response) => {
        const candidates = Array.isArray(response.data) ? response.data : [];
        commit('SET_CANDIDATES', candidates);
      });
    },

    createRequest({ commit, dispatch }, payload) {
      commit('SET_PROCESSING', true);
      const formData = new FormData();
      utils.buildFormData(formData, payload);

      return dkoApi
        .create(formData)
        .then((response) => {
          dispatch(
            'snackbar/showSnackbar',
            { message: ['Wniosek został utworzony'], type: 'success' },
            { root: true }
          );
          router.push({ name: 'dko-cooperation-index' });
          return response.data;
        })
        .finally(() => {
          commit('SET_PROCESSING', false);
        });
    },

    updateRequest({ commit, dispatch, state }, payload) {
      commit('SET_PROCESSING', true);
      return dkoApi
        .update(state.request.id, payload)
        .then((response) => {
          commit('SET_REQUEST', response.data);
          dispatch(
            'snackbar/showSnackbar',
            { message: ['Wniosek został zapisany'], type: 'success' },
            { root: true }
          );
          return response.data;
        })
        .finally(() => {
          commit('SET_PROCESSING', false);
        });
    },

    performAction({ commit, dispatch, state }, actionData) {
      dispatch('changeActionStatus', true, { root: true });
      return dkoApi
        .performAction(state.request.id, {
          ...actionData,
          oldStatus: state.request.status,
        })
        .then((response) => {
          commit('SET_ACTIONS', response.data.actions);
          dispatch('dialog/clearDialog', null, { root: true });
          dispatch(
            'snackbar/showSnackbar',
            { message: ['Akcja wykonana'], type: 'success' },
            { root: true }
          );
          return dispatch('fetchOne', state.request.id);
        })
        .catch((error) => {
          if (error.response?.status === 409 && state.request?.id) {
            dispatch('fetchOne', state.request.id);
            dispatch('fetchActions', state.request.id);
            dispatch('dialog/clearDialog', null, { root: true });
          }
          return Promise.reject(error);
        })
        .finally(() => {
          dispatch('changeActionStatus', false, { root: true });
        });
    },

    createMember({ dispatch, state }, payload) {
      return dkoApi
        .createMember({ ...payload, dkoCooperationRequestId: state.request.id })
        .then(() => {
          dispatch('dialog/clearDialog', null, { root: true });
          dispatch(
            'snackbar/showSnackbar',
            { message: ['Dodano osobę do zespołu'], type: 'success' },
            { root: true }
          );
          return Promise.all([
            dispatch('fetchOne', state.request.id),
            dispatch('fetchActions', state.request.id),
          ]);
        });
    },

    deleteMember({ dispatch, state }, { id, listType }) {
      return dkoApi
        .deleteMember(id, listType)
        .then(() =>
          Promise.all([
            dispatch('fetchOne', state.request.id),
            dispatch('fetchActions', state.request.id),
          ])
        );
    },
  },
};
